import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { KpiInfo } from '../../../../components/delivery/widgets/KpiTile';
import { MOD_HISTORY_LABELS, MOD_HISTORY_TIPS } from '../../../../constants/deliveryMoney';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { fieldIsMasked } from '../../../../lib/access/types';
import { useViewProjection } from '../../../../lib/access/useViewProjection';
import { formatUtcYmd } from '../../_shared/api/nullable';
import { useFieldLineage } from '../../_shared/hooks/lineageAuditHooks';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { useProgramModifications } from '../../Docs/hooks';
// The four period field paths a committed change set can write — see
// applyPeriodOfPerformanceField in pangaea's
// delivery_contract_change_repository.go, which accepts exactly these and
// warning-skips anything else. Each one gets its own GetFieldLineage call and
// the rows are joined back together by modification below.
const MONEY_FIELDS = [
    'funded_amount',
    'total_amount'
];
const DATE_FIELDS = [
    'start_date',
    'end_date'
];
// SAD-300: the optional-task-exercise event field path — see
// recordOptionalTaskExerciseForModSLIN / resolvePeriodForExercise in pangaea's
// delivery_contract_change_repository.go. Its value_json is a multi-key
// object (task/SLIN identity, funded_amount only when authoritative), never a
// single scalar, so it is read via parseExerciseValue below, not
// parseLineageScalar.
const EXERCISE_FIELD = 'optional_task_exercise';
const signedCurrency = (n)=>`${n >= 0 ? '+' : '−'}${formatMoney(Math.abs(n))}`;
// A lineage row's source id is a 32-character opaque hex string. The
// attribution ladder falls back to it when a write cannot be matched to a
// contract modification number, and rendering it WHOLE is what pushed this
// table past the side card's edge and clipped the last two columns — the exact
// failure this surface was already fixed for once. The row stays fully
// traceable: data-modification-id carries the untruncated value, and so does
// the cell's title attribute. A short id (a test fixture's 'cs-9', say) is left
// alone; there is nothing to shorten.
const SHORT_ID_CHARS = 8;
const shortenSourceId = (id)=>id.length > SHORT_ID_CHARS + 1 ? `${id.slice(0, SHORT_ID_CHARS)}…` : id;
// Matt asked for colour on this surface ("The biggest thing is visual … with
// color etc.", 2026-08-28). A funding delta's SIGN is the one thing here that
// is genuinely directional, so it is the one thing tinted: money added reads
// success, money removed reads danger, and an exactly-zero change stays
// neutral because it is neither. Everything else — running totals, current
// values, dates — is absolute, not directional, and stays untinted so the
// colour keeps meaning something.
//
// This must only ever be applied INSIDE a <MaskedField>. Tinting the cell
// itself would paint the sign onto a masked viewer's "Restricted" placeholder
// and leak the direction of a figure they are not allowed to see — the same
// class of leak as the data-running-total attribute bug.
const deltaTone = (n)=>n > 0 ? 'text-success' : n < 0 ? 'text-danger' : '';
// "(+30 days)" — the mockup annotates an end-date move with how much period it
// bought. Returns '' when either side is missing or the move is same-day, so a
// first sighting of an end date (no previous value to measure from) says
// nothing rather than inventing a span.
function dayDelta(from, to) {
    if (!from || !to) return '';
    const a = Date.parse(`${from}T00:00:00Z`);
    const b = Date.parse(`${to}T00:00:00Z`);
    if (Number.isNaN(a) || Number.isNaN(b)) return '';
    const days = Math.round((b - a) / 86400000);
    if (days === 0) return '';
    return `${days > 0 ? '+' : '−'}${Math.abs(days)} days`;
}
// The timeline's node sits OUTSIDE its MaskedField — it is a bare <span>, not a
// figure — so unlike deltaTone it cannot inherit the mask by nesting. Passing
// null paints it neutral, and the caller passes null whenever funded_amount is
// hidden: a coloured dot beside a "Restricted" figure would hand a masked
// viewer the direction of a number they are not allowed to see, which is the
// data-running-total leak wearing a different hat.
const nodeTone = (n)=>n === null ? 'bg-muted-foreground' : n > 0 ? 'bg-success' : n < 0 ? 'bg-danger' : 'bg-muted-foreground';
// value_json rides pangaea's decodeChangeValue-flexible encoding — a bare JSON
// number, a JSON-quoted string, or a single-key {field: val} object. This
// mirrors just enough of that flexibility to render a scalar; it is a
// read-only display, not a re-implementation of the backend's coercion.
function parseLineageScalar(valueJson) {
    if (!valueJson) return null;
    let parsed;
    try {
        parsed = JSON.parse(valueJson);
    } catch  {
        parsed = valueJson;
    }
    if (typeof parsed === 'object' && parsed !== null) {
        const values = Object.values(parsed);
        parsed = values.length === 1 ? values[0] : undefined;
    }
    return parsed;
}
// EXERCISE_FIELD's value_json is a multi-key object (optionalTaskExerciseValueJSON,
// pangaea's delivery_contract_change_repository.go) — it must NOT go through
// parseLineageScalar, which deliberately unwraps a single-key object to a
// scalar and would silently drop every key but one here.
function parseExerciseValue(valueJson) {
    if (!valueJson) return null;
    let parsed;
    try {
        parsed = JSON.parse(valueJson);
    } catch  {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null) return null;
    const v = parsed;
    const taskName = typeof v.task_name === 'string' ? v.task_name : null;
    const taskNumber = typeof v.task_number === 'number' ? v.task_number : null;
    const clinNumber = typeof v.clin_number === 'string' ? v.clin_number : null;
    if (!taskName && taskNumber === null && !clinNumber) return null;
    return {
        taskName,
        taskNumber,
        clinNumber
    };
}
const stampOf = (writtenAt)=>({
        seconds: Number(writtenAt?.seconds ?? 0),
        nanos: writtenAt?.nanos ?? 0
    });
// Sub-second ordering matters: a single apply transaction writes every field
// of one modification within the same second, so seconds alone would shuffle
// the funded/total/date rows of the same mod against each other.
const compareStamps = (a, b)=>a.seconds !== b.seconds ? a.seconds - b.seconds : a.nanos - b.nanos;
// Collapse the flat per-field lineage writes into one event per modification,
// ordered by when they were applied. Writes that carry no modification or
// change-set id (manual/unattributed) are grouped by source kind and time so
// they still surface rather than silently vanishing.
//
// EXERCISE_FIELD is the one field path that can legitimately repeat within a
// single modification — one mod can exercise more than one optional task in
// the same period — so its writes ACCUMULATE into exerciseValueJsons rather
// than overwriting a single map slot the way every other (single-valued)
// field path does.
function buildEvents(writes) {
    const byKey = new Map();
    for (const w of writes){
        const key = w.sourceId || `${w.sourceKind}@${w.at.seconds}`;
        const existing = byKey.get(key);
        if (existing) {
            if (w.fieldPath === EXERCISE_FIELD) {
                existing.exerciseValueJsons.push(w.valueJson);
            } else {
                existing.values.set(w.fieldPath, parseLineageScalar(w.valueJson));
            }
            // The event sorts at its EARLIEST write — the moment the mod landed.
            if (compareStamps(w.at, existing.at) < 0) existing.at = w.at;
            continue;
        }
        byKey.set(key, {
            key,
            sourceId: w.sourceId,
            sourceKind: w.sourceKind,
            at: w.at,
            values: w.fieldPath === EXERCISE_FIELD ? new Map() : new Map([
                [
                    w.fieldPath,
                    parseLineageScalar(w.valueJson)
                ]
            ]),
            exerciseValueJsons: w.fieldPath === EXERCISE_FIELD ? [
                w.valueJson
            ] : []
        });
    }
    return [
        ...byKey.values()
    ].sort((a, b)=>compareStamps(a.at, b.at));
}
// null and '' are NOT zero. Number(null) and Number('') are both 0, so
// coercing them would invent a $0.00 write: the row would show a delta of
// -(previous value) and, worse, reset the running total every later row is
// measured against. A lineage entry whose value_json is empty or null is a
// field this modification did not write.
const asNumber = (v)=>{
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const asDate = (v)=>{
    if (typeof v !== 'string' || !v.trim()) return null;
    const ymd = v.trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : v.trim();
};
/**
 * Matt's table (SAD-137, 2026-08-27): one row per modification in order,
 * showing that mod's CHANGE — never a running total, which is D7's
 * non-cumulative rule for period rows. The running total appears only in the
 * timeline underneath, and in the trailing current-values row.
 */ function buildRows(events, modLookup) {
    const rows = [];
    // Previous absolute value per field, carried forward so a mod that touched
    // only funding still reports an accurate PoP for the next date change.
    let prevFunded = null;
    let prevTotal = null;
    // Previous PoP dates, so an end-date move can be annotated with the days it
    // added. Same running-previous pattern the money deltas already use.
    let prevStart = '';
    let prevEnd = '';
    let sawAward = false;
    for (const ev of events){
        const funded = asNumber(ev.values.get('funded_amount'));
        const total = asNumber(ev.values.get('total_amount'));
        const start = asDate(ev.values.get('start_date'));
        const end = asDate(ev.values.get('end_date'));
        const isMod = ev.sourceKind === 'Modification';
        // The first non-modification write is the initial contract. Matt asked for
        // that row to read 'Award' rather than a mod number.
        const isAward = !isMod && !sawAward;
        if (isAward) sawAward = true;
        // Attribution ladder, unchanged in spirit from the per-field tables this
        // replaced: the modification's real number when we can resolve it, else
        // the raw source id so the row is still traceable, else an explicit
        // manual/unattributed label — never a raw empty cell.
        const meta = ev.sourceId ? modLookup.get(ev.sourceId) : undefined;
        // Rung 2 of the ladder (the raw source id) is the only rung that can carry
        // an opaque 32-character value, so it is the only one that gets shortened.
        const unmatchedId = !isAward && !meta?.number ? ev.sourceId : '';
        const modLabel = isAward ? MOD_HISTORY_LABELS.award : meta?.number || unmatchedId && shortenSourceId(unmatchedId) || 'Manual / unattributed';
        const modTitle = unmatchedId || undefined;
        // Matt's mockup writes a date move as "<from> → <to>", annotates an end-date
        // move with the number of days it added or removed, and says "No change"
        // rather than leaving a dash when a modification touched money only.
        const popParts = [];
        if (start) popParts.push(`Start → ${start}`);
        if (end) {
            const days = dayDelta(prevEnd, end);
            popParts.push(`End → ${end}${days ? ` (${days})` : ''}`);
        }
        if (end) prevEnd = end;
        if (start && !prevStart) prevStart = start;
        const exercises = ev.exerciseValueJsons.map(parseExerciseValue).filter((e)=>e !== null);
        // Stable, deterministic order independent of whatever order the backend
        // happened to return the lineage rows in: ascending task number (an
        // exercise with no task number sorts last), then CLIN number as a
        // tiebreak — the same order a PM would naturally read the task list in.
        exercises.sort((a, b)=>{
            if (a.taskNumber !== b.taskNumber) {
                if (a.taskNumber === null) return 1;
                if (b.taskNumber === null) return -1;
                return a.taskNumber - b.taskNumber;
            }
            return (a.clinNumber ?? '').localeCompare(b.clinNumber ?? '');
        });
        const exerciseLabels = exercises.map((exercise)=>[
                exercise.taskNumber !== null ? `Optional Task ${exercise.taskNumber} exercised` : 'Optional task exercised',
                exercise.clinNumber ? `SLIN ${exercise.clinNumber}` : null
            ].filter(Boolean).join(' — '));
        // The delta is measured against the last value this field actually held.
        // A mod that did not touch a field contributes no delta for it (null, not
        // zero) — "unchanged" and "changed by nothing" read differently to a PM.
        const fundedDelta = funded === null ? null : funded - (prevFunded ?? 0);
        const totalDelta = total === null ? null : total - (prevTotal ?? 0);
        if (funded !== null) prevFunded = funded;
        if (total !== null) prevTotal = total;
        rows.push({
            key: ev.key,
            modId: ev.sourceId,
            modLabel,
            modTitle,
            isAward,
            effectiveDate: meta?.effectiveDate ?? '',
            popChange: popParts.join(' · '),
            exerciseLabels,
            fundedDelta,
            totalDelta,
            fundedRunning: prevFunded
        });
    }
    return rows;
}
// Five columns have to live inside a side card, so headers wrap rather than
// forcing the table wider than the drawer and clipping the last money column.
// The table still sits in an overflow-x-auto container as the final backstop.
const HEAD_CELL = 'px-1.5 py-1 text-left align-bottom font-medium';
const BODY_CELL = 'px-1.5 py-1 align-top';
// Money reads right-aligned with tabular figures so digits line up column-wise
// and magnitudes can be compared down the column at a glance — the whole point
// of a delta column a PM scans rather than reads.
const MONEY_CELL = `${BODY_CELL} text-right tabular-nums`;
const MONEY_HEAD_CELL = 'px-1.5 py-1 text-right align-bottom font-medium';
// The Award row states where the period STARTED; every later row states how
// much a modification moved it. Matt's mockup draws that difference: the award
// figures are plain and unsigned, and only the changes are signed and tinted.
function DeltaFigure({ value, isAward }) {
    if (value === null) return /*#__PURE__*/ _jsx("span", {
        children: "—"
    });
    if (isAward) return /*#__PURE__*/ _jsx("span", {
        children: formatMoney(value)
    });
    return /*#__PURE__*/ _jsx("span", {
        className: deltaTone(value),
        children: signedCurrency(value)
    });
}
function HeaderCell({ label, tip, dataName, money = false }) {
    return /*#__PURE__*/ _jsx("th", {
        scope: "col",
        className: money ? MONEY_HEAD_CELL : HEAD_CELL,
        children: /*#__PURE__*/ _jsxs("span", {
            className: "inline-flex items-baseline gap-1",
            children: [
                label,
                /*#__PURE__*/ _jsx(KpiInfo, {
                    label: label,
                    tip: tip,
                    testid: Testids.deliveryShared.infoTip,
                    dataName: dataName
                })
            ]
        })
    });
}
/**
 * The vertical funding timeline matt asked for beneath the table: one node per
 * modification showing what that mod did to funding and the running total
 * after it. The running total lives HERE and in the current-values row only —
 * never in the table's delta columns (D7: per-period rows are non-cumulative).
 */ function FundingTimeline({ rows, resource }) {
    // data-running-total exists so journeys can read the absolute figure without
    // parsing formatted currency. It must obey the SAME mask the visible figure
    // obeys — otherwise a masked viewer sees "Restricted" on screen while the
    // real dollars sit in the DOM attribute. Fail closed while the projection is
    // loading, exactly as MaskedField does.
    const { data: projection } = useViewProjection(resource);
    const fundedHidden = !projection || projection.viewMode === 'no_view' || fieldIsMasked(projection.viewMode, projection.maskedFields, 'funded_amount');
    const funding = rows.filter((r)=>r.fundedDelta !== null);
    if (funding.length === 0) return null;
    // Matt's mockup scales each bar against the LARGEST CHANGE, not against the
    // running total: its shortest bar is the −$142k step and its longest is the
    // +$1.565M one, even though that row's running total is far from the largest.
    // The Award row is excluded from the scale and drawn as an empty track — it
    // is the baseline every later bar is measured against, not a change.
    const widestChange = Math.max(...funding.filter((r)=>!r.isAward).map((r)=>Math.abs(r.fundedDelta ?? 0)), 0);
    const barPercent = (r)=>{
        if (r.isAward || widestChange === 0 || r.fundedDelta === null) return 0;
        return Math.max(2, Math.abs(r.fundedDelta) / widestChange * 100);
    };
    // Net change since award: the last running total minus the award baseline.
    // Read off the same rows the bars use, so it can never disagree with them.
    const award = funding.find((r)=>r.isAward);
    const last = funding[funding.length - 1];
    const netSinceAward = award?.fundedRunning !== null && award?.fundedRunning !== undefined && last?.fundedRunning !== null ? (last?.fundedRunning ?? 0) - award.fundedRunning : null;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliveryOperational.popModHistoryTimeline,
        className: "mt-5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-2 flex items-baseline justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1 text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                        children: [
                            MOD_HISTORY_LABELS.timelineHeading,
                            /*#__PURE__*/ _jsx(KpiInfo, {
                                label: MOD_HISTORY_LABELS.timelineHeading,
                                tip: MOD_HISTORY_TIPS.timeline,
                                testid: Testids.deliveryShared.infoTip,
                                dataName: "fundingTimeline"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[10.5px] text-muted-foreground",
                        children: MOD_HISTORY_LABELS.runningFundedTotal
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ol", {
                className: "space-y-2.5",
                children: funding.map((r)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.deliveryOperational.popModHistoryTimelineNode,
                        "data-modification-id": r.modId || 'unattributed',
                        ...fundedHidden || r.fundedRunning === null ? {} : {
                            'data-running-total': String(r.fundedRunning)
                        },
                        className: "grid grid-cols-[auto_10px_1fr_auto] items-start gap-x-2 text-xs",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "pt-px tabular-nums text-[11px] text-muted-foreground",
                                children: r.effectiveDate || '—'
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                "aria-hidden": "true",
                                className: "relative flex h-full justify-center",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "absolute top-3 h-full w-px bg-border"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `relative mt-1 h-2.5 w-2.5 shrink-0 rounded-full border-2 border-background ${nodeTone(fundedHidden || r.isAward ? null : r.fundedDelta)}`
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-baseline gap-x-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: r.modLabel
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "tabular-nums font-medium",
                                                children: /*#__PURE__*/ _jsx(MaskedField, {
                                                    fieldName: "funded_amount",
                                                    resource: resource,
                                                    children: r.fundedDelta === null ? '—' : r.isAward ? // The award is a starting position, not a change: shown
                                                    // unsigned and untinted, exactly as the mockup draws it.
                                                    formatMoney(r.fundedDelta) : /*#__PURE__*/ _jsx("span", {
                                                        className: deltaTone(r.fundedDelta),
                                                        children: signedCurrency(r.fundedDelta)
                                                    })
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-1 h-1 w-full rounded-full bg-muted",
                                        children: !fundedHidden && barPercent(r) > 0 && /*#__PURE__*/ _jsx("div", {
                                            "data-testid": Testids.deliveryOperational.popModHistoryTimelineBar,
                                            className: `h-1 rounded-full ${(r.fundedDelta ?? 0) < 0 ? 'bg-danger' : 'bg-success'}`,
                                            style: {
                                                width: `${barPercent(r)}%`
                                            }
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "tabular-nums text-[11px] text-muted-foreground",
                                children: /*#__PURE__*/ _jsx(MaskedField, {
                                    fieldName: "funded_amount",
                                    resource: resource,
                                    children: r.fundedRunning !== null ? formatMoney(r.fundedRunning) : '—'
                                })
                            })
                        ]
                    }, r.key))
            }),
            netSinceAward !== null && /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.deliveryOperational.popModHistoryNetChange,
                className: "mt-3 flex items-baseline justify-between gap-2 border-t border-border pt-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                        children: MOD_HISTORY_LABELS.netFundingChange
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "tabular-nums text-sm font-medium",
                        children: /*#__PURE__*/ _jsx(MaskedField, {
                            fieldName: "funded_amount",
                            resource: resource,
                            children: /*#__PURE__*/ _jsx("span", {
                                className: deltaTone(netSinceAward),
                                children: signedCurrency(netSinceAward)
                            })
                        })
                    })
                ]
            })
        ]
    });
}
/**
 * Per-period modification history — SAD-137's answer to matt's "it does not
 * appear to be robust enough" bounce (2026-08-27).
 *
 * Reads the four period field paths' lineage (funded_amount, total_amount,
 * start_date, end_date) and joins them BY MODIFICATION so each row is one
 * mod's full effect on this period, rather than the previous per-field
 * sub-tables. MOD # and Effective Date come from ListProgramModifications,
 * matched on the lineage row's source_modification_id.
 *
 * Every query is scoped to this ONE period's row id, so nothing here can
 * become cumulative across periods.
 */ export function PeriodModificationHistory({ period, programId, resource }) {
    const funded = useFieldLineage({
        targetObjectType: 'PeriodOfPerformance',
        targetObjectId: period.id,
        targetFieldPath: MONEY_FIELDS[0]
    });
    const total = useFieldLineage({
        targetObjectType: 'PeriodOfPerformance',
        targetObjectId: period.id,
        targetFieldPath: MONEY_FIELDS[1]
    });
    const startDate = useFieldLineage({
        targetObjectType: 'PeriodOfPerformance',
        targetObjectId: period.id,
        targetFieldPath: DATE_FIELDS[0]
    });
    const endDate = useFieldLineage({
        targetObjectType: 'PeriodOfPerformance',
        targetObjectId: period.id,
        targetFieldPath: DATE_FIELDS[1]
    });
    const exercise = useFieldLineage({
        targetObjectType: 'PeriodOfPerformance',
        targetObjectId: period.id,
        targetFieldPath: EXERCISE_FIELD
    });
    const mods = useProgramModifications(programId);
    // The modification list is part of the table's data, not a decoration:
    // MOD # and Effective Date come from it. Rendering before it resolves shows
    // raw ids and blank dates that then flip under the reader.
    const queries = [
        funded,
        total,
        startDate,
        endDate,
        exercise
    ];
    const isLoading = queries.some((q)=>q.isLoading) || mods.isLoading;
    // The money columns are the point of this table; if BOTH money lookups are
    // forbidden or failing there is nothing honest to render. A failure on the
    // date lookups alone degrades to a blank PoP column instead of blanking the
    // whole section — same per-field independence the previous version had.
    const moneyUnavailable = funded.isError && total.isError;
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-xs text-muted-foreground",
            children: "Loading…"
        });
    }
    if (moneyUnavailable) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliveryOperational.popModHistorySection,
            className: "space-y-2",
            children: [
                /*#__PURE__*/ _jsx(SectionHeading, {}),
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.deliveryOperational.popModHistoryUnavailable,
                    className: "text-xs italic text-muted-foreground",
                    children: "Modification history unavailable."
                })
            ]
        });
    }
    const writes = [];
    for (const [q, fieldPath] of [
        [
            funded,
            MONEY_FIELDS[0]
        ],
        [
            total,
            MONEY_FIELDS[1]
        ],
        [
            startDate,
            DATE_FIELDS[0]
        ],
        [
            endDate,
            DATE_FIELDS[1]
        ],
        [
            exercise,
            EXERCISE_FIELD
        ]
    ]){
        for (const group of q.data?.groups ?? []){
            for (const entry of group.entries){
                writes.push({
                    fieldPath,
                    sourceKind: group.sourceKind,
                    sourceId: entry.sourceModificationId || entry.sourceChangeSetId || '',
                    valueJson: entry.valueJson,
                    at: stampOf(entry.writtenAt)
                });
            }
        }
    }
    const modLookup = new Map();
    for (const m of mods.data?.items ?? []){
        const eff = m.effectiveDate ? formatUtcYmd(m.effectiveDate) ?? '' : '';
        modLookup.set(m.id, {
            number: m.modificationNumber || '',
            effectiveDate: eff
        });
    }
    const rows = buildRows(buildEvents(writes), modLookup);
    const currentPop = [
        period.startDate,
        period.endDate
    ].map((d)=>d ? formatUtcYmd(d) ?? '' : '').filter(Boolean).join(' – ');
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliveryOperational.popModHistorySection,
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx(SectionHeading, {}),
            rows.length === 0 && /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.deliveryOperational.popModHistoryEmpty,
                className: "text-xs italic text-muted-foreground",
                children: "No modifications have changed this period yet — current values below."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    "data-testid": Testids.deliveryOperational.popModHistoryTable,
                    className: "w-full text-xs",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                className: "text-muted-foreground",
                                children: [
                                    /*#__PURE__*/ _jsx(HeaderCell, {
                                        label: MOD_HISTORY_LABELS.modNumber,
                                        tip: MOD_HISTORY_TIPS.modNumber,
                                        dataName: "modNumber"
                                    }),
                                    /*#__PURE__*/ _jsx(HeaderCell, {
                                        label: MOD_HISTORY_LABELS.effectiveDate,
                                        tip: MOD_HISTORY_TIPS.effectiveDate,
                                        dataName: "effectiveDate"
                                    }),
                                    /*#__PURE__*/ _jsx(HeaderCell, {
                                        label: MOD_HISTORY_LABELS.popChange,
                                        tip: MOD_HISTORY_TIPS.popChange,
                                        dataName: "popChange"
                                    }),
                                    /*#__PURE__*/ _jsx(HeaderCell, {
                                        label: MOD_HISTORY_LABELS.fundedDelta,
                                        tip: MOD_HISTORY_TIPS.fundedDelta,
                                        dataName: "fundedDelta",
                                        money: true
                                    }),
                                    /*#__PURE__*/ _jsx(HeaderCell, {
                                        label: MOD_HISTORY_LABELS.totalValueDelta,
                                        tip: MOD_HISTORY_TIPS.totalValueDelta,
                                        dataName: "totalValueDelta",
                                        money: true
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("tbody", {
                            children: [
                                rows.map((r)=>/*#__PURE__*/ _jsxs("tr", {
                                        "data-testid": r.isAward ? Testids.deliveryOperational.popModHistoryAwardRow : Testids.deliveryOperational.popModHistoryRow,
                                        "data-modification-id": r.modId || 'unattributed',
                                        className: `border-t border-border/60 ${r.isAward ? 'bg-muted/40' : ''}`,
                                        children: [
                                            /*#__PURE__*/ _jsx("td", {
                                                className: `${BODY_CELL} font-medium`,
                                                title: r.modTitle,
                                                children: r.modLabel
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: BODY_CELL,
                                                children: r.effectiveDate || '—'
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: BODY_CELL,
                                                children: r.popChange || /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    children: MOD_HISTORY_LABELS.noPopChange
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: MONEY_CELL,
                                                children: /*#__PURE__*/ _jsx(MaskedField, {
                                                    fieldName: "funded_amount",
                                                    resource: resource,
                                                    children: /*#__PURE__*/ _jsx(DeltaFigure, {
                                                        value: r.fundedDelta,
                                                        isAward: r.isAward
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("td", {
                                                className: MONEY_CELL,
                                                children: /*#__PURE__*/ _jsx(MaskedField, {
                                                    fieldName: "total_amount",
                                                    resource: resource,
                                                    children: /*#__PURE__*/ _jsx(DeltaFigure, {
                                                        value: r.totalDelta,
                                                        isAward: r.isAward
                                                    })
                                                })
                                            })
                                        ]
                                    }, r.key)),
                                /*#__PURE__*/ _jsxs("tr", {
                                    "data-testid": Testids.deliveryOperational.popModHistoryCurrentRow,
                                    className: "border-t border-border bg-muted/30 font-medium",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: BODY_CELL,
                                            children: MOD_HISTORY_LABELS.current
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: BODY_CELL
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: BODY_CELL,
                                            children: currentPop || '—'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: MONEY_CELL,
                                            children: /*#__PURE__*/ _jsx(MaskedField, {
                                                fieldName: "funded_amount",
                                                resource: resource,
                                                children: formatMoney(period.fundedAmount)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: MONEY_CELL,
                                            children: /*#__PURE__*/ _jsx(MaskedField, {
                                                fieldName: "total_amount",
                                                resource: resource,
                                                children: formatMoney(period.totalAmount)
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(FundingTimeline, {
                rows: rows,
                resource: resource
            })
        ]
    });
}
function SectionHeading() {
    return /*#__PURE__*/ _jsx("div", {
        className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
        children: "Modification History"
    });
}
